import { useState } from "react";
import axios from "axios";
import { X, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUM_OPTIONS = ["Under $100M","$100M – $500M","$500M – $1B","$1B – $2B","Over $2B"];
const SERVICE_OPTIONS = [
  { value: "finance", label: "Finance Operations" },
  { value: "legal", label: "Legal Operations" },
  { value: "both", label: "Both (Finance + Legal)" },
];
const SOFTWARE_OPTIONS = ["Investran","eFront","Allvue","Geneva","Juniper Square","Yardi","Other"];

export default function ApplyModal({ onClose }) {
  const [form, setForm] = useState({ firm_name:"", contact_name:"", email:"", phone:"", aum_range:"", service_type:"", software_stack:[], notes:"" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.firm_name.trim()) e.firm_name = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.aum_range) e.aum_range = "Required";
    if (!form.service_type) e.service_type = "Please select a service";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const toggleSoftware = (sw) => {
    setForm((f) => ({ ...f, software_stack: f.software_stack.includes(sw) ? f.software_stack.filter((s) => s !== sw) : [...f.software_stack, sw] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSuccess(true);
    } catch {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0a0a0a] border border-white/10 rounded-xl text-white px-4 py-3 text-sm outline-none focus:border-[#00FF66]/50 transition-colors placeholder-gray-600";
  const labelCls = "block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2";

  return (
    <div className="modal-overlay" data-testid="apply-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" data-testid="apply-modal">
        {success ? (
          <div className="p-12 text-center" data-testid="apply-success">
            <div className="w-16 h-16 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/20 flex items-center justify-center mx-auto mb-6">
              <Check size={28} className="text-[#00FF66]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Application Submitted!</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">
              We'll review your application within 24 hours and reach out to match you with your dedicated X Analyst operator.
            </p>
            <button onClick={onClose} className="btn-primary px-8 py-3 rounded-full" data-testid="success-close-btn">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Get Started with X Analyst</h2>
                <p className="text-gray-500 text-sm">First month free. Takes 5 minutes. We respond within 24 hours.</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-white transition-colors ml-4 mt-1" data-testid="modal-close-btn">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Firm Name *</label>
                  <input className={inputCls} name="firm_name" value={form.firm_name} onChange={handleChange} placeholder="Magnolia Capital" data-testid="input-firm-name" />
                  {errors.firm_name && <p className="text-red-400 text-xs mt-1">{errors.firm_name}</p>}
                </div>
                <div>
                  <label className={labelCls}>Your Name *</label>
                  <input className={inputCls} name="contact_name" value={form.contact_name} onChange={handleChange} placeholder="John Smith" data-testid="input-contact-name" />
                  {errors.contact_name && <p className="text-red-400 text-xs mt-1">{errors.contact_name}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Work Email *</label>
                  <input className={inputCls} name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@firm.com" data-testid="input-email" />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className={labelCls}>Phone (Optional)</label>
                  <input className={inputCls} name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (212) 555-0100" data-testid="input-phone" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Firm AUM *</label>
                <select className={inputCls + " cursor-pointer"} name="aum_range" value={form.aum_range} onChange={handleChange} data-testid="select-aum">
                  <option value="">Select AUM range...</option>
                  {AUM_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {errors.aum_range && <p className="text-red-400 text-xs mt-1">{errors.aum_range}</p>}
              </div>

              <div>
                <label className={labelCls}>Service Needed *</label>
                <div className="space-y-2">
                  {SERVICE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-3 cursor-pointer text-sm text-gray-400 hover:text-white transition-colors">
                      <input type="radio" name="service_type" value={opt.value} checked={form.service_type === opt.value} onChange={handleChange} className="accent-[#00FF66]" data-testid={`radio-${opt.value}`} />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {errors.service_type && <p className="text-red-400 text-xs mt-1">{errors.service_type}</p>}
              </div>

              <div>
                <label className={labelCls}>Current Software Stack</label>
                <div className="flex flex-wrap gap-2">
                  {SOFTWARE_OPTIONS.map((sw) => (
                    <button key={sw} type="button" onClick={() => toggleSoftware(sw)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.software_stack.includes(sw) ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]" : "border-white/10 text-gray-400 hover:border-white/30"}`}
                      data-testid={`checkbox-${sw.toLowerCase()}`}>
                      {sw}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Additional Notes</label>
                <textarea className={inputCls} name="notes" value={form.notes} onChange={handleChange}
                  placeholder="Tell us about your fund structure, specific workflows, deal types..."
                  rows={3} style={{ resize: "vertical" }} data-testid="textarea-notes" />
              </div>

              {errors.submit && <p className="text-red-400 text-sm">{errors.submit}</p>}
            </div>

            <div className="px-6 pb-6">
              <button type="submit" disabled={loading} className="btn-primary w-full py-4 rounded-full text-base disabled:opacity-60 disabled:cursor-not-allowed" data-testid="submit-application-btn">
                {loading ? "Submitting..." : "Submit Application →"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
